/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Game from '../Game/Game';
import RecoveryManager from '../Game/RecoveryManager';
import Currency from '../Localization/Currency';
import { IPlayState } from '../Server/Object/PlayResult';
import PlayResultComponent from '../Server/PlayResultComponent';
import StateComponent from '../StateManager/StateComponent';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import Bangup from './Bangup';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Manages the award meter(s) and bangup(s) used for displaying payouts. */
@cc._decorator.ccclass
@cc._decorator.requireComponent(Bangup)
export default class WinMeter extends StateDependencyComponent
{
    /** The primary state for displaying payout awards. */
    @property({ type: StateReference, visible: true })
    private _awardState: StateReference = new StateReference(StateID.Award, true);

    /** Any additional award states, for bonus for example. */
    @property({ type: [StateReference], visible: true })
    private _altAwardStates: StateReference[] = [];

    /** Delay to wait before completing any award states so the user has time to process. */
    @property ({ type: cc.Float, visible: true })
    private _stateDelay: number = 1.2;

    /** The controller for the bangup on any win meters. */
    private bangup: Bangup = null;

    /** @hidden */
    private _gameRef: InjectableComponent<Game> = new InjectableComponent<Game>();
    /** @hidden */
    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent<PlayResultComponent>();
    /** @hidden */
    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent<RecoveryManager>();

    /** @hidden */
    protected setContexts(): void
    {
        this._gameRef.SetContext(Game);
        this._playResultRef.SetContext(PlayResultComponent);
        this._recoveryManagerRef.SetContext(RecoveryManager);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._altAwardStates, [this._awardState]);

        this.bangup = this.getComponent(Bangup);
        this._gameRef.WithValue(game =>
        {
            this.onTargetEvent(
                game.AdvancePlayEventDispatcher,
                Game.AdvancePlayEventName,
                this.advancePlayEventHandler);
        });
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        const isAwardState: boolean = event.State === this._awardState.Value
            || this._altAwardStates.some((x) => x.Value === event.State);

        if (isAwardState)
        {
            this.startBangup(event.State);
        }
    }

    /** @hidden */
    private advancePlayEventHandler = (event: cc.Event.EventCustom): void =>
    {
        const stateReference: StateReference =
            (this._awardState.Value.IsCurrentState())
                ? this._awardState
                : this._altAwardStates.find(StateReference.IsCurrentFilter);

        if (!stateReference) { return; }

        this.bangup.QuickBang();
        this.scheduleOnce(() => this.completeState(stateReference.Value), this._stateDelay);
    }

    /**
     * Used to manually start the award bangup based on the current play result.
     */
    private startBangup(activeState: StateComponent): void
    {
        const playState: IPlayState = this._playResultRef.Value.PlayState;
        let winAmount: Currency = new Currency(this.bangup.BetManagerRef.Value.ActiveDenomObservable, playState.winMc);

        if (this.bangup.UpdateBalance && !this.bangup.IsCumulative)
        {
            this.bangup.SetTarget(winAmount);
        }
        else
        {
            this.bangup.BangupAdditionalValue(winAmount.Difference(this.bangup.TargetValue));
        }

        if (this._recoveryManagerRef.Value.Recovering)
        {
            this.bangup.QuickBang();
            this.completeState(activeState);
        }
        else if (winAmount.GetValue() < 1)
        {
            this.completeState(activeState);
        }
        else
        {
            this.scheduleOnce(() => this.completeState(activeState), this._stateDelay);
        }
    }
}
